import { useEffect, useMemo, useState } from "react";
import { apiJson } from "../../utils/api.js";
import { GroupRow } from "./GroupRow.jsx";
import { TelephonySettingsSection } from "./TelephonySettingsSection.jsx";
import { AuditLogsPanel } from "./AuditLogsPanel.jsx";
import { FunnelBroadcastSection } from "./FunnelBroadcastSection.jsx";

function pill(text) {
  return <div className="pill">{text}</div>;
}

/** Длительность по секундам (отметка status_changed_at в UTC). refreshTick — для перерисовки каждую секунду. */
function formatRusDurationFromIso(statusChangedAtIso, refreshTick) {
  void refreshTick;
  if (!statusChangedAtIso) return "—";
  const t = new Date(statusChangedAtIso).getTime();
  if (Number.isNaN(t)) return "—";
  let s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  const days = Math.floor(s / 86400);
  s %= 86400;
  const h = Math.floor(s / 3600);
  s %= 3600;
  const min = Math.floor(s / 60);
  const sec = s % 60;
  const parts = [];
  if (days > 0) parts.push(`${days} д`);
  if (h > 0) parts.push(`${h} ч`);
  if (min > 0) parts.push(`${min} мин`);
  if (parts.length === 0 && sec > 0) parts.push(`${sec} сек`);
  if (parts.length === 0) parts.push("0 сек");
  return parts.join(" ");
}

const PlusIcon = (
  <svg
    className="sectionTitleBtnSvg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </svg>
);

const SearchIcon = (
  <svg
    className="inputIconSvg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);

const TrashIcon = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 6h18" />
    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
    <line x1="10" x2="10" y1="11" y2="17" />
    <line x1="14" x2="14" y1="11" y2="17" />
  </svg>
);

const PencilIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
    <path d="m15 5 4 4" />
  </svg>
);

export function AdminSettings() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [tab, setTab] = useState("team"); // team | telephony | shifts | dictionaries | logs

  const [managers, setManagers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [tags, setTags] = useState([]);

  const [managersFormOpen, setManagersFormOpen] = useState(false);
  const [groupsFormOpen, setGroupsFormOpen] = useState(false);
  const [tagsFormOpen, setTagsFormOpen] = useState(false);

  const [managerSearchQ, setManagerSearchQ] = useState("");
  const [foundUser, setFoundUser] = useState(null);
  const [managerSearchNotFound, setManagerSearchNotFound] = useState(false);
  const [assignGroupId, setAssignGroupId] = useState("");
  const [assignRole, setAssignRole] = useState("manager");
  const [searchBusy, setSearchBusy] = useState(false);
  const [assignBusy, setAssignBusy] = useState(false);

  const [newGroupTitle, setNewGroupTitle] = useState("");
  const [addGroupBusy, setAddGroupBusy] = useState(false);

  const [newTagTitle, setNewTagTitle] = useState("");
  const [addTagBusy, setAddTagBusy] = useState(false);

  const [assignToGroupId, setAssignToGroupId] = useState({}); // managerId -> selected group id for "add to department"
  const [assignToGroupBusy, setAssignToGroupBusy] = useState({}); // managerId -> boolean
  const [editingDeptManagerId, setEditingDeptManagerId] = useState(null); // id менеджера, у которого открыто редактирование отдела

  const [rejectionReasons, setRejectionReasons] = useState([]);
  const [reasonsFormOpen, setReasonsFormOpen] = useState(false);
  const [newReasonTitle, setNewReasonTitle] = useState("");
  const [addReasonBusy, setAddReasonBusy] = useState(false);

  const [presence, setPresence] = useState(null);
  const [presenceError, setPresenceError] = useState(null);
  const [presenceLoading, setPresenceLoading] = useState(false);
  const [presenceTick, setPresenceTick] = useState(0);

  async function loadPresence() {
    setPresenceLoading(true);
    setPresenceError(null);
    try {
      const data = await apiJson("/api/admin/managers/presence");
      setPresence(data);
    } catch (e) {
      setPresenceError(e instanceof Error ? e.message : String(e));
      setPresence(null);
    } finally {
      setPresenceLoading(false);
    }
  }

  async function reloadAll() {
    setLoading(true);
    setError(null);
    try {
      const data = await apiJson("/api/admin/settings");
      const m = data.managers?.items || [];
      const g = data.groups?.items || [];
      const t = data.tags?.items || [];
      const r = data.rejection_reasons || [];
      setManagers(m);
      setGroups(g);
      setTags(t);
      setRejectionReasons(r);
      if (g.length && !assignGroupId) {
        setAssignGroupId(String(g[0].id));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reloadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tab !== "shifts") return undefined;
    loadPresence();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => {
    if (tab !== "shifts") return undefined;
    const id = setInterval(() => setPresenceTick((x) => x + 1), 1000);
    return () => clearInterval(id);
  }, [tab]);

  const groupOptions = useMemo(() => groups || [], [groups]);

  return (
    <main className="chat" id="settingsScreen">
      <div className="crmStickyStack">
        <div className="reportsTabs dealsFunnelTabs">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "team"}
            className={`reportsTab ${tab === "team" ? "active" : ""}`}
            onClick={() => setTab("team")}
          >
            Команда
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "telephony"}
            className={`reportsTab ${tab === "telephony" ? "active" : ""}`}
            onClick={() => setTab("telephony")}
          >
            Телефония
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "shifts"}
            className={`reportsTab ${tab === "shifts" ? "active" : ""}`}
            onClick={() => setTab("shifts")}
          >
            Смены
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "dictionaries"}
            className={`reportsTab ${tab === "dictionaries" ? "active" : ""}`}
            onClick={() => setTab("dictionaries")}
          >
            Справочники
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "broadcast"}
            className={`reportsTab ${tab === "broadcast" ? "active" : ""}`}
            onClick={() => setTab("broadcast")}
          >
            Рассылки
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "logs"}
            className={`reportsTab ${tab === "logs" ? "active" : ""}`}
            onClick={() => setTab("logs")}
          >
            Логи
          </button>
        </div>
      </div>
      <div className="section">
        <div className="crmListSurface" id="list" style={{ border: "none", background: "none" }}>
          {error ? <div className="settingsBlock">{pill(`Ошибка: ${error}`)}</div> : null}

          {tab === "logs" ? (
            <div className="settingsBlock">
              <div className="sectionTitleRow">
                <span className="sectionTitle">Логи событий</span>
              </div>
              <AuditLogsPanel />
            </div>
          ) : null}

          {tab === "team" ? (
            <>
          {/* Managers */}
          <div className="settingsBlock">
            <div className="sectionTitleRow">
              <span className="sectionTitle">Менеджеры</span>
              <button
                type="button"
                className={`chip ${managersFormOpen ? "active" : ""}`}
                id="toggleManagersForm"
                aria-label={managersFormOpen ? "Скрыть добавление менеджера" : "Добавить менеджера"}
                onClick={() => setManagersFormOpen((v) => !v)}
              >
                {managersFormOpen ? "Скрыть" : "Добавить"}
              </button>
            </div>

            <div className="crmMeta" style={{ marginTop: 10 }}>
              Добавление менеджера: найдите пользователя по <b>tg_id</b> или <b>@username</b>, выберите отдел и нажмите «Назначить».
              Администраторы из <b>ADMIN_IDS</b> в .env отображаются с меткой «Админ»; их роль не меняется на «менеджер» при перезапуске.
            </div>

            <div id="managersList">
              {!loading && !error && (!managers.length ? pill("Нет менеджеров") : null)}
              {!loading &&
                !error &&
                managers.map((m) => {
                  const name = m.display_name || m.code || "—";
                  const href = m.username
                    ? `https://t.me/${String(m.username).replace(/^@/, "")}`
                    : `tg://user?id=${m.telegram_id}`;
                  const groupTitles = (m.group_titles || []).join(", ") || "—";
                  const selectedGroupId = assignToGroupId[m.id] ?? (groups.length ? String(groups[0].id) : "");
                  const addToDeptBusy = assignToGroupBusy[m.id];
                  return (
                    <div className="settingsRow settingsRowManager" key={String(m.id)}>
                      <div className="managerRowLeft">
                        <div className="managerRowNameRow">
                          <a className="managerNameLink" href={href} target="_blank" rel="noopener">
                            {name}
                          </a>
                          {m.role === "admin" ? (
                            <span className="pill pillCompact">Админ</span>
                          ) : m.role === "rop" ? (
                            <span className="pill pillCompact">РОП</span>
                          ) : m.role === "moderator" ? (
                            <span className="pill pillCompact">Модератор</span>
                          ) : null}
                        </div>
                        <div className="managerRowDeptRow" title={groupTitles}>
                          <span className="managerDeptText">{groupTitles}</span>
                        </div>
                      </div>
                      <div className="settingsRowActions managerRowActions">
                        {editingDeptManagerId === m.id && groups.length > 0 ? (
                          <>
                            <select
                              className="input managerAssignDeptSelect"
                              value={selectedGroupId}
                              onChange={(e) => setAssignToGroupId((prev) => ({ ...prev, [m.id]: e.target.value }))}
                              disabled={addToDeptBusy}
                              title="Назначить в отдел"
                            >
                              {groups.map((g) => (
                                <option key={String(g.id)} value={String(g.id)}>
                                  {g.title}
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              className="smallBtn"
                              disabled={addToDeptBusy || !selectedGroupId}
                              title="Добавить менеджера в выбранный отдел"
                              onClick={async () => {
                                if (!selectedGroupId) return;
                                setAssignToGroupBusy((prev) => ({ ...prev, [m.id]: true }));
                                try {
                                  const targetGid = Number(selectedGroupId);
                                  const prevDept =
                                    m.department_group_id != null &&
                                    m.department_group_id !== undefined
                                      ? Number(m.department_group_id)
                                      : null;
                                  if (
                                    prevDept != null &&
                                    !Number.isNaN(prevDept) &&
                                    prevDept !== targetGid
                                  ) {
                                    await apiJson(
                                      `/api/admin/groups/${prevDept}/members/${m.id}`,
                                      { method: "DELETE" },
                                    );
                                  }
                                  await apiJson(`/api/admin/groups/${selectedGroupId}/members`, {
                                    method: "POST",
                                    headers: { "Content-Type": "application/json" },
                                    body: JSON.stringify({ manager_id: m.id }),
                                  });
                                  setEditingDeptManagerId(null);
                                  await reloadAll();
                                } catch (e) {
                                  window.alert(e instanceof Error ? e.message : String(e));
                                } finally {
                                  setAssignToGroupBusy((prev) => ({ ...prev, [m.id]: false }));
                                }
                              }}
                            >
                              В отдел
                            </button>
                            <button
                              type="button"
                              className="smallBtn managerEditDeptCancelBtn"
                              title="Отмена"
                              onClick={() => setEditingDeptManagerId(null)}
                            >
                              Отмена
                            </button>
                          </>
                        ) : groups.length > 0 ? (
                          <button
                            type="button"
                            className="smallBtn managerEditDeptBtn"
                            title="Изменить отдел"
                            aria-label="Изменить отдел"
                            onClick={() => setEditingDeptManagerId(m.id)}
                          >
                            <span className="managerEditDeptBtnSvg">{PencilIcon}</span>
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="smallBtn danger managerDemoteBtn"
                          title="Понизить"
                          aria-label="Понизить"
                          onClick={async () => {
                            if (!window.confirm("Понизить до обычного пользователя (забрать доступы)?")) return;
                            try {
                              await apiJson(`/api/admin/managers/${m.id}/demote`, { method: "POST" });
                              await reloadAll();
                            } catch (e) {
                              window.alert(String(e));
                            }
                          }}
                        >
                          {TrashIcon}
                        </button>
                      </div>
                    </div>
                  );
                })}
            </div>

            <div className={`settingsForm ${managersFormOpen ? "" : "hidden"}`} id="managersForm">
              <div className="inputWithIcon inputWithIconSearch">
                <input
                  type="text"
                  className="input"
                  id="newManagerSearch"
                  placeholder="tg_id или @username"
                  autoComplete="off"
                  value={managerSearchQ}
                  onChange={(e) => setManagerSearchQ(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      document.getElementById("searchManagerBtn")?.click();
                    }
                  }}
                />
                <button
                  type="button"
                  className="inputIcon"
                  id="searchManagerBtn"
                  title="Поиск"
                  aria-label="Поиск"
                  disabled={searchBusy}
                  onClick={async () => {
                    const q = managerSearchQ.trim();
                    if (!q) {
                      window.alert("Введите tg_id или @username.");
                      return;
                    }
                    setSearchBusy(true);
                    setManagerSearchNotFound(false);
                    setFoundUser(null);
                    try {
                      const data = await apiJson(`/api/admin/users/search?q=${encodeURIComponent(q)}`);
                      setFoundUser(data);
                      setManagerSearchNotFound(false);
                      if (groupOptions.length) setAssignGroupId(String(groupOptions[0].id));
                    } catch {
                      setFoundUser(null);
                      setManagerSearchNotFound(true);
                    } finally {
                      setSearchBusy(false);
                    }
                  }}
                >
                  {SearchIcon}
                </button>
              </div>
            </div>

            <div
              className={`managerSearchResult ${foundUser || managerSearchNotFound ? "" : "hidden"}`}
              id="managerSearchResult"
            >
              <div
                className={`managerSearchNotFound ${managerSearchNotFound ? "" : "hidden"}`}
                id="managerSearchNotFound"
              >
                {pill("Пользователь не найден")}
              </div>
              <div
                className={`managerSearchResultRow ${foundUser ? "" : "hidden"}`}
                id="managerSearchResultRow"
              >
                <div className="managerSearchMetaLine" id="managerSearchUser">
                  <div className="managerSearchNameBlock">
                    <span className="managerSearchName">{foundUser?.display}</span>
                  </div>
                  <select
                    className="input managerSearchRoleSelect"
                    id="managerAssignRole"
                    value={assignRole}
                    onChange={(e) => setAssignRole(e.target.value)}
                    title="Роль"
                    aria-label="Роль"
                  >
                    <option value="manager">Менеджер</option>
                    <option value="rop">РОП</option>
                    <option value="moderator">Модератор</option>
                  </select>
                </div>
                <div className="managerSearchAssignLine">
                  <select
                    className="input managerSearchGroupSelect"
                    id="managerAssignGroup"
                    value={assignGroupId}
                    onChange={(e) => setAssignGroupId(e.target.value)}
                    title="Отдел"
                    aria-label="Отдел"
                  >
                    {groupOptions.length ? (
                      groupOptions.map((g) => (
                        <option key={String(g.id)} value={String(g.id)}>
                          {g.title}
                        </option>
                      ))
                    ) : (
                      <option value="">— отдел —</option>
                    )}
                  </select>
                  <button
                    type="button"
                    className="smallBtn managerSearchAssignBtn"
                    id="assignManagerBtn"
                    disabled={assignBusy}
                    onClick={async () => {
                      if (!foundUser) {
                        window.alert("Сначала найдите пользователя.");
                        return;
                      }
                      setAssignBusy(true);
                      try {
                        const groupId = String(assignGroupId || "").trim();
                        const res = await apiJson("/api/admin/managers", {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({
                            telegram_id: foundUser.telegram_id,
                            group_id: groupId ? Number(groupId) : undefined,
                            role: assignRole,
                          }),
                        });
                        if (res?.note) window.alert(String(res.note));
                        setManagerSearchQ("");
                        setFoundUser(null);
                        setManagerSearchNotFound(false);
                        await reloadAll();
                      } catch (e) {
                        window.alert(String(e));
                      } finally {
                        setAssignBusy(false);
                      }
                    }}
                  >
                    Назначить
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Отделы */}
          <div className="settingsBlock">
            <div className="sectionTitleRow">
              <span className="sectionTitle">Отделы</span>
              <button
                type="button"
                className={`chip ${groupsFormOpen ? "active" : ""}`}
                id="toggleGroupsForm"
                aria-label={groupsFormOpen ? "Скрыть добавление отдела" : "Добавить отдел"}
                onClick={() => setGroupsFormOpen((v) => !v)}
              >
                {groupsFormOpen ? "Скрыть" : "Добавить"}
              </button>
            </div>

            <div className={`settingsForm ${groupsFormOpen ? "" : "hidden"}`} id="groupsForm">
              <div className="inputWithIcon inputWithIconAdd">
                <input
                  type="text"
                  className="input"
                  id="newGroupTitle"
                  placeholder="Название отдела"
                  value={newGroupTitle}
                  onChange={(e) => setNewGroupTitle(e.target.value)}
                />
                <button
                  type="button"
                  className="inputIcon"
                  id="addGroupBtn"
                  title="Добавить отдел"
                  aria-label="Добавить отдел"
                  disabled={addGroupBusy}
                  onClick={async () => {
                    const title = newGroupTitle.trim();
                    if (!title) {
                      window.alert("Укажите название отдела.");
                      return;
                    }
                    setAddGroupBusy(true);
                    try {
                      await apiJson("/api/admin/groups", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ title }),
                      });
                      setNewGroupTitle("");
                      await reloadAll();
                    } catch (e) {
                      window.alert(String(e));
                    } finally {
                      setAddGroupBusy(false);
                    }
                  }}
                >
                  {PlusIcon}
                </button>
              </div>
            </div>

            <div id="groupsList">
              {!loading && !error && (!groups.length ? pill("Нет отделов") : null)}
              {!loading &&
                !error &&
                groups.map((g) => (
                  <GroupRow
                    key={String(g.id)}
                    group={g}
                    allManagers={managers}
                    onChanged={reloadAll}
                  />
                ))}
            </div>
          </div>

            </>
          ) : null}

          {tab === "telephony" ? (
            <TelephonySettingsSection
              groups={groups}
              managers={managers}
              loading={loading}
              error={error}
              onReload={reloadAll}
            />
          ) : null}

          {tab === "broadcast" ? <FunnelBroadcastSection /> : null}

          {tab === "shifts" ? (
            <div className="settingsBlock">
              <div className="sectionTitleRow">
                <span className="sectionTitle">Статусы смен</span>
                <button
                  type="button"
                  className={`chip ${presenceLoading ? "active" : ""}`}
                  disabled={presenceLoading}
                  aria-busy={presenceLoading}
                  onClick={() => loadPresence()}
                >
                  {presenceLoading ? "…" : "Обновить"}
                </button>
              </div>
              {presenceError ? <div className="settingsBlock" style={{ marginTop: 8 }}>{pill(`Ошибка: ${presenceError}`)}</div> : null}
              <div id="presenceList" style={{ marginTop: 12 }}>
                {!presenceLoading && !presenceError && presence && !presence.items?.length ? pill("Нет сотрудников") : null}
                {!presenceError &&
                  presence?.items?.map((row) => {
                    const name = row.display_name || row.code || `id ${row.id}`;
                    const roleLabel =
                      row.role === "admin"
                        ? "Админ"
                        : row.role === "rop"
                          ? "РОП"
                          : row.role === "moderator"
                            ? "Модератор"
                            : "Менеджер";
                    const active = row.work_day_active === true;
                    const dur = formatRusDurationFromIso(row.status_changed_at, presenceTick);
                    return (
                      <div
                        className="settingsRow settingsRowManager"
                        key={String(row.id)}
                        style={row.is_active === false ? { opacity: 0.65 } : undefined}
                      >
                        <div className="managerRowLeft">
                          <div className="managerRowNameRow">
                            <span className="managerNameLink" style={{ cursor: "default" }}>
                              {name}
                            </span>
                            <span className="pill pillCompact">{roleLabel}</span>
                            {row.is_active === false ? (
                              <span className="pill pillCompact" title="Доступ отключён">
                                неактивен
                              </span>
                            ) : null}
                          </div>
                          <div className="managerRowDeptRow">
                            <span className="managerDeptText" style={{ fontSize: "13px", color: "var(--crm-muted, #888)" }}>
                              {active ? "Работает" : "Не работает"}
                              {row.status_changed_at ? ` · ${dur}` : ""}
                            </span>
                          </div>
                        </div>
                        <div className="settingsRowActions managerRowActions">
                          <span
                            className="pill"
                            style={
                              active
                                ? { background: "rgba(34, 160, 80, 0.2)", color: "inherit" }
                                : { background: "rgba(120, 120, 120, 0.2)", color: "inherit" }
                            }
                            title={active ? "Рабочий день начат" : "Рабочий день не начат"}
                          >
                            {active ? "В смене" : "Вне смены"}
                          </span>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          ) : null}

          {tab === "dictionaries" ? (
            <>
          {/* Tags */}
          <div className="settingsBlock">
            <div className="sectionTitleRow">
              <span className="sectionTitle">Теги</span>
              <button
                type="button"
                className={`chip ${tagsFormOpen ? "active" : ""}`}
                id="toggleTagsForm"
                aria-label={tagsFormOpen ? "Скрыть добавление тега" : "Добавить тег"}
                onClick={() => setTagsFormOpen((v) => !v)}
              >
                {tagsFormOpen ? "Скрыть" : "Добавить"}
              </button>
            </div>

            <div className={`settingsForm ${tagsFormOpen ? "" : "hidden"}`} id="tagsForm">
              <div className="inputWithIcon inputWithIconAdd">
                <input
                  type="text"
                  className="input"
                  id="newTagTitle"
                  placeholder="Название тега"
                  value={newTagTitle}
                  onChange={(e) => setNewTagTitle(e.target.value)}
                />
                <button
                  type="button"
                  className="inputIcon"
                  id="addTagBtn"
                  title="Добавить тег"
                  aria-label="Добавить тег"
                  disabled={addTagBusy}
                  onClick={async () => {
                    const title = newTagTitle.trim();
                    if (!title) {
                      window.alert("Укажите название тега.");
                      return;
                    }
                    setAddTagBusy(true);
                    try {
                      await apiJson("/api/admin/tags", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ title }),
                      });
                      setNewTagTitle("");
                      await reloadAll();
                    } catch (e) {
                      window.alert(String(e));
                    } finally {
                      setAddTagBusy(false);
                    }
                  }}
                >
                  {PlusIcon}
                </button>
              </div>
            </div>

            <div id="tagsList">
              {!loading && !error && (!tags.length ? pill("Нет тегов. Добавьте первый.") : null)}
              {!loading &&
                !error &&
                tags.map((t) => (
                  <div className="settingsRow settingsRowTag" key={String(t.id)}>
                    <span className="tagTitle">{t.title}</span>
                    <button
                      type="button"
                      className="tagDeleteBtn"
                      title="Удалить"
                      onClick={async () => {
                        if (!window.confirm(`Удалить тег «${t.title}»?`)) return;
                        try {
                          await apiJson(`/api/admin/tags/${t.id}`, { method: "DELETE" });
                          await reloadAll();
                        } catch (e) {
                          window.alert(String(e));
                        }
                      }}
                    >
                      {TrashIcon}
                    </button>
                  </div>
                ))}
            </div>
          </div>

          {/* Причины отказов */}
          <div className="settingsBlock">
            <div className="sectionTitleRow">
              <span className="sectionTitle">Причины отказов</span>
              <button
                type="button"
                className={`chip ${reasonsFormOpen ? "active" : ""}`}
                title="Добавить причину"
                aria-label={reasonsFormOpen ? "Скрыть добавление причины" : "Добавить причину"}
                onClick={() => setReasonsFormOpen((v) => !v)}
              >
                {reasonsFormOpen ? "Скрыть" : "Добавить"}
              </button>
            </div>
            <div className={`settingsForm ${reasonsFormOpen ? "" : "hidden"}`} id="rejectionReasonsForm">
              <div className="inputWithIcon inputWithIconAdd">
                <input
                  type="text"
                  className="input"
                  placeholder="Название причины"
                  value={newReasonTitle}
                  onChange={(e) => setNewReasonTitle(e.target.value)}
                />
                <button
                  type="button"
                  className="inputIcon"
                  title="Добавить причину"
                  aria-label="Добавить причину"
                  disabled={addReasonBusy}
                  onClick={async () => {
                    const title = newReasonTitle.trim();
                    if (!title) {
                      window.alert("Укажите название причины.");
                      return;
                    }
                    setAddReasonBusy(true);
                    try {
                      await apiJson("/api/admin/rejection-reasons", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ title }),
                      });
                      setNewReasonTitle("");
                      await reloadAll();
                    } catch (e) {
                      window.alert(String(e));
                    } finally {
                      setAddReasonBusy(false);
                    }
                  }}
                >
                  {PlusIcon}
                </button>
              </div>
            </div>
            <div id="rejectionReasonsList">
              {!loading && !error && (!rejectionReasons.length ? pill("Нет причин. Добавьте первую.") : null)}
              {!loading &&
                !error &&
                rejectionReasons.map((r) => (
                  <div className="settingsRow settingsRowTag" key={String(r.id)}>
                    <span className="tagTitle">{r.title}</span>
                    <button
                      type="button"
                      className="tagDeleteBtn"
                      title="Удалить"
                      aria-label="Удалить"
                      onClick={async () => {
                        if (!window.confirm(`Удалить причину «${r.title}»?`)) return;
                        try {
                          await apiJson(`/api/admin/rejection-reasons/${r.id}`, { method: "DELETE" });
                          await reloadAll();
                        } catch (e) {
                          window.alert(String(e));
                        }
                      }}
                    >
                      {TrashIcon}
                    </button>
                  </div>
                ))}
            </div>
          </div>
            </>
          ) : null}
        </div>
      </div>
    </main>
  );
}
